import type { inferProcedureInput, inferProcedureOutput } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../utils/trpc'

type BaseRouter = AppRouter['admin']['cycleParticipants']
type InitialData = inferProcedureOutput<BaseRouter['initialize']>
export type Participant = InitialData['participants']['data'][number]
export type SearchParticipantsQuery = inferProcedureInput<BaseRouter['search']>
export type User = InitialData['users']['data'][number]
export type SearchUsersQuery = inferProcedureInput<BaseRouter['users']['search']>
type AddParticipantsQuery = inferProcedureInput<BaseRouter['create']>

interface PreDeleteCheckResult {
	participantsWithData: string[]
	participantsWithoutData: string[]
	cycleStatus: string
	// Mirrors the per-org `participant-soft-delete` feature flag. When false, the
	// RemoveParticipantDialog renders the refusal copy instead of the mode picker.
	softDeleteEnabled: boolean
}

interface CycleParticipantsState {
	cycleId: string
	organizationId: string
	participants: InitialData['participants']
	selectedParticipants: string[]
	searchParticipantsQuery: SearchParticipantsQuery
	searchParticipantsTimeout: NodeJS.Timeout
	users: InitialData['users']
	searchUsersQuery: SearchUsersQuery
	searchUsersTimeout: NodeJS.Timeout
	addParticipantsQuery: AddParticipantsQuery
	forms: InitialData['forms']
	customFields: InitialData['customFields']
	selectedCustomField: {
		name: string
		possibleValues: string[]
		selectedValues: string[]
	}
	customFieldsFilters: Record<string, string[]>
	error: string
	loading: boolean
	preDeleteCheckResult: PreDeleteCheckResult | null
	preDeleteCheckLoading: boolean
	deleteLoading: boolean
	clearError: () => void

	initialize(input: { cycleId: string; organizationId: string }): Promise<void>
	setSearchParticipantsQuery(params?: Omit<SearchParticipantsQuery, 'cycleId'>): void
	searchParticipants(): Promise<void>
	setSelectedParticipants(selection: Participant[]): void
	runPreDeleteCheck(): Promise<PreDeleteCheckResult | null>
	deleteSelectedParticipants(mode?: 'display' | 'hide'): Promise<boolean>
	setSearchUsersQuery(params?: Omit<SearchUsersQuery, 'cycleId' | 'organizationId'>): void
	searchUsers(): Promise<void>
	setSelectedUsers(selected: User[], selection?: { excludedIds: Set<string> }): Promise<void>
	addParticipants(): Promise<void>
	setFormAssignment(formId: string): void
	searchNextForms(): Promise<void>
	setSelectedCustomField(field: string): Promise<void>
	setSelectedCustomFieldSelectedValues(selectedValues: string[]): void
	addCustomFieldFilter(): void
	removeCustomFieldFilter(name: string): void
}

const api = trpcClient.admin.cycleParticipants
export const createCycleParticipantsStore = create<CycleParticipantsState>()(
	devtools(
		(set, get) => ({
			participants: [],
			selectedParticipants: [],
			participantsSearchQuery: {},
			participantsSearchTimeout: undefined,
			users: [],
			addParticipantsQuery: undefined,
			usersSearchQuery: undefined,
			usersSearchTimeout: undefined,
			forms: [],
			customFields: [],
			selectedCustomField: undefined,
			customFieldFilters: undefined,
			error: undefined,
			loading: false,
			preDeleteCheckResult: null,
			preDeleteCheckLoading: false,
			deleteLoading: false,

			async initialize(input) {
				set({
					error: undefined,
					loading: true,
					searchParticipantsQuery: {
						cycleId: input.cycleId,
					},
					addParticipantsQuery: {
						formId: '',
						cycleId: input.cycleId,
						organizationId: input.organizationId,
						selectAllMode: false,
						include: [],
					},
					searchUsersQuery: {
						cycleId: input.cycleId,
						organizationId: input.organizationId,
					},
					cycleId: input.cycleId,
					organizationId: input.organizationId,
				})
				try {
					const initialData = await api.initialize.query(input)

					set({
						participants: initialData.participants,
						users: initialData.users,
						forms: initialData.forms,
						customFields: initialData.customFields,
						loading: false,
					})
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Error initializing cycleParticipants',
						loading: false,
					})
				}
			},

			setSearchParticipantsQuery(params) {
				set(({ searchParticipants, searchParticipantsQuery, searchParticipantsTimeout }) => {
					if (searchParticipantsTimeout) {
						clearTimeout(searchParticipantsTimeout)
					}

					return {
						searchParticipantsQuery: {
							...searchParticipantsQuery,
							...params,
						},
						searchParticipantsTimeout: setTimeout(searchParticipants, 300),
					}
				})
			},

			async searchParticipants() {
				const { searchParticipantsQuery } = get()

				set({
					participants: await api.search.query(searchParticipantsQuery),
				})
			},

			setSelectedParticipants(selectedParticipants) {
				set({
					selectedParticipants: selectedParticipants.map(({ id }) => id),
				})
			},

			async runPreDeleteCheck() {
				const { cycleId, organizationId, selectedParticipants } = get()
				set({ preDeleteCheckLoading: true, preDeleteCheckResult: null })

				try {
					const result = await api.preDeleteCheck.mutate({
						cycleId,
						organizationId,
						participantIds: selectedParticipants,
					})
					set({ preDeleteCheckResult: result, preDeleteCheckLoading: false })
					return result
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Error checking participant data',
						preDeleteCheckLoading: false,
					})
					return null
				}
			},

			async deleteSelectedParticipants(mode) {
				const { cycleId, organizationId, selectedParticipants } = get()
				set({ deleteLoading: true })

				try {
					await api.delete.mutate({
						cycleId,
						organizationId,
						participantIds: selectedParticipants,
						mode,
					})
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Error deleting cycle participants',
						deleteLoading: false,
					})
					return false
				}

				// Delete committed on the server. A failure to refresh the participant list
				// must NOT make this method report failure: the caller would skip the
				// success toast and leave a stale list showing participants that are
				// already gone (a retry would then 404). Swallow refresh errors here;
				// the next user-driven query will recover.
				try {
					const updated = await api.search.query({ cycleId })
					set({ participants: updated })
				} catch (refreshError) {
					console.warn('deleteSelectedParticipants: refresh after delete failed', refreshError)
				}

				set({
					selectedParticipants: [],
					preDeleteCheckResult: null,
					deleteLoading: false,
				})
				return true
			},

			setSearchUsersQuery(params) {
				set(({ searchUsersQuery, searchUsers, searchUsersTimeout }) => {
					if (searchUsersTimeout) {
						clearTimeout(searchUsersTimeout)
					}

					return {
						searchUsersQuery: {
							...searchUsersQuery,
							...params,
						},
						searchUsersTimeout: setTimeout(searchUsers, 300),
					}
				})
			},

			async searchUsers() {
				const { searchUsersQuery, customFieldsFilters } = get()

				set({
					users: await api.users.search.query({
						...searchUsersQuery,
						customFieldsFilters,
					}),
				})
			},

			clearError() {
				set({
					error: undefined,
				})
			},

			setSelectedUsers(users, selection) {
				const { cycleId, organizationId, customFieldsFilters } = get()

				if (selection) {
					return set({
						addParticipantsQuery: {
							formId: '',
							cycleId,
							organizationId,
							selectAllMode: true,
							include: {
								customFieldsFilters,
							},
							exclude: {
								userIds: [...selection.excludedIds.values()],
							},
						},
					})
				}

				set({
					addParticipantsQuery: {
						formId: '',
						cycleId,
						organizationId,
						selectAllMode: false,
						include: users.map(({ id }) => id),
					},
				})
			},

			setFormAssignment(formId) {
				set(({ addParticipantsQuery }) => ({
					addParticipantsQuery: {
						...addParticipantsQuery,
						formId,
					},
				}))
			},

			async searchNextForms() {
				const { organizationId, forms } = get()
				const totalPages = Math.ceil(forms.meta.total / forms.meta.perPage)

				if (forms.data.length < forms.meta.total && forms.meta.page < totalPages) {
					const moreForms = await api.forms.search.query({
						organizationId,
						page: ++forms.meta.page,
					})

					set({
						forms: {
							data: [...forms.data, ...moreForms.data],
							meta: moreForms.meta,
						},
					})
				}
			},

			async addParticipants() {
				const { addParticipantsQuery } = get()

				try {
					const participants = await api.create.mutate(addParticipantsQuery)

					set({
						participants,
						addParticipantsQuery: {
							formId: '',
							cycleId: addParticipantsQuery.cycleId,
							organizationId: addParticipantsQuery.organizationId,
							selectAllMode: false,
							include: [],
						},
					})
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Unknown error adding cycle participants',
					})
				}
			},

			async setSelectedCustomField(fieldName) {
				const { organizationId } = get()
				try {
					set({
						selectedCustomField: {
							name: fieldName,
							possibleValues: await api.users.getCustomFieldValues.query({
								organizationId,
								fieldName,
							}),
							selectedValues: [],
						},
					})
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Error loading custom field values',
					})
				}
			},

			setSelectedCustomFieldSelectedValues(selectedValues) {
				set(({ selectedCustomField }) => ({
					selectedCustomField: {
						...selectedCustomField,
						selectedValues,
					},
				}))
			},

			addCustomFieldFilter() {
				set(({ customFieldsFilters, selectedCustomField, searchUsers, searchUsersTimeout }) => {
					if (searchUsersTimeout) {
						clearTimeout(searchUsersTimeout)
					}

					return {
						customFieldsFilters: {
							...customFieldsFilters,
							[selectedCustomField.name]: selectedCustomField.selectedValues,
						},
						selectedCustomField: undefined,
						searchUsersTimeout: setTimeout(searchUsers, 300),
					}
				})
			},

			removeCustomFieldFilter(name) {
				set(({ customFieldsFilters, searchUsers, searchUsersTimeout }) => {
					if (searchUsersTimeout) {
						clearTimeout(searchUsersTimeout)
					}

					return {
						customFieldsFilters: Object.fromEntries(
							Object.entries(customFieldsFilters).filter(([key]) => key !== name),
						),
						searchUsersTimeout: setTimeout(searchUsers, 300),
					}
				})
			},
		}),
		{ name: 'cycle-participants-tab' },
	),
)
